import React, { useMemo, useState } from 'react';
import { B } from '../constants';
import { flights as flightsApi, bookings } from '../api';

// "PT9H30M" → "9h 30m"
function formatDuration(iso) {
  if (!iso) return '';
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?/.exec(iso);
  if (!match) return iso;
  const [, h, m] = match;
  return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(' ') || '0m';
}

function formatTime(iso) {
  if (!iso) return '';
  // Trim seconds and TZ for display
  return iso.slice(11, 16);
}

function formatDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function offerTotalDuration(offer) {
  return offer.itineraries.reduce((acc, it) => acc + iso8601ToMinutes(it.duration), 0);
}

function iso8601ToMinutes(iso) {
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?/.exec(iso || '');
  if (!match) return 0;
  return Number(match[1] || 0) * 60 + Number(match[2] || 0);
}

export default function FlightResults({
  offers,
  loading,
  searchId,
  onSelect,
  selectedOfferId,
}) {
  const [sortBy, setSortBy] = useState('price');
  const [airlineFilter, setAirlineFilter] = useState('');
  const [trackingId, setTrackingId] = useState(null);

  const airlines = useMemo(() => {
    const set = new Set();
    (offers || []).forEach((o) => (o.validatingAirlineCodes || []).forEach((c) => set.add(c)));
    return Array.from(set);
  }, [offers]);

  const sorted = useMemo(() => {
    const filtered = (offers || []).filter((o) => {
      if (!airlineFilter) return true;
      return (o.validatingAirlineCodes || []).includes(airlineFilter);
    });
    const copy = [...filtered];
    if (sortBy === 'price') {
      copy.sort((a, b) => Number(a.price?.total || 0) - Number(b.price?.total || 0));
    } else if (sortBy === 'duration') {
      copy.sort((a, b) => offerTotalDuration(a) - offerTotalDuration(b));
    } else if (sortBy === 'stops') {
      copy.sort(
        (a, b) =>
          Math.max(...a.itineraries.map((it) => it.stops)) -
          Math.max(...b.itineraries.map((it) => it.stops)),
      );
    }
    return copy;
  }, [offers, sortBy, airlineFilter]);

  const handleBook = async (offer) => {
    setTrackingId(offer.id);
    try {
      await flightsApi.track({
        searchId,
        offerId: offer.id,
        partner: 'skyscanner',
        origin: offer.itineraries?.[0]?.segments?.[0]?.departure?.iataCode,
        destination:
          offer.itineraries?.[0]?.segments?.[offer.itineraries[0].segments.length - 1]?.arrival
            ?.iataCode,
        priceCents: Math.round(Number(offer.price?.total || 0) * 100),
        currency: offer.price?.currency,
      });
      // Record the booking intent so creator attribution gets a row. The
      // actual reservation happens on the partner site.
      try {
        await bookings.create({
          partner: 'skyscanner',
          productType: 'flight',
          productName: `${offer.itineraries?.[0]?.segments?.[0]?.departure?.iataCode || ''} → ${
            offer.itineraries?.[0]?.segments?.[offer.itineraries[0].segments.length - 1]?.arrival
              ?.iataCode || ''
          }`,
          amountCents: Math.round(Number(offer.price?.total || 0) * 100) || 1,
          currency: offer.price?.currency || 'USD',
        });
      } catch (e) {
        // Non-fatal — user can still click through to the partner.
      }
    } finally {
      setTrackingId(null);
    }
    if (offer.bookingLink) {
      window.open(offer.bookingLink, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: B.muted }}>Searching for flights…</div>
    );
  }
  if (!offers) return null;
  if (offers.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: B.muted }}>
        No flights matched your search.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: B.muted }}>{sorted.length} results</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
          >
            <option value="price">Sort: Cheapest</option>
            <option value="duration">Sort: Shortest</option>
            <option value="stops">Sort: Fewest stops</option>
          </select>
          {airlines.length > 1 && (
            <select
              value={airlineFilter}
              onChange={(e) => setAirlineFilter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
            >
              <option value="">All airlines</option>
              {airlines.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
        {sorted.map((offer) => {
          const isSelected = selectedOfferId === offer.id;
          return (
            <li
              key={offer.id}
              style={{
                background: 'white',
                borderRadius: 12,
                padding: 16,
                border: isSelected ? `2px solid ${B.forest}` : '1px solid #eee',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  {offer.itineraries.map((it, idx) => {
                    const first = it.segments[0];
                    const last = it.segments[it.segments.length - 1];
                    return (
                      <div key={idx} style={{ marginBottom: idx < offer.itineraries.length - 1 ? 10 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 16 }}>
                            {formatTime(first?.departure?.at)} → {formatTime(last?.arrival?.at)}
                          </strong>
                          <span style={{ color: B.muted, fontSize: 13 }}>
                            {first?.departure?.iataCode} – {last?.arrival?.iataCode}
                          </span>
                          <span style={{ color: B.muted, fontSize: 13 }}>
                            • {formatDuration(it.duration)}
                          </span>
                          <span style={{ color: B.muted, fontSize: 13 }}>
                            • {it.stops === 0 ? 'Nonstop' : `${it.stops} stop${it.stops > 1 ? 's' : ''}`}
                          </span>
                        </div>
                        <div style={{ color: B.muted, fontSize: 12, marginTop: 2 }}>
                          {first?.carrierName || first?.carrierCode} {first?.number} • {formatDate(first?.departure?.at)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 140 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: B.forest }}>
                    {offer.price?.currency} {Number(offer.price?.total || 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 11, color: B.muted }}>total for trip</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {onSelect && (
                      <button
                        type="button"
                        onClick={() => onSelect(offer)}
                        style={{
                          padding: '8px 12px',
                          background: isSelected ? B.forest : 'transparent',
                          color: isSelected ? 'white' : B.forest,
                          border: `1px solid ${B.forest}`,
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {isSelected ? 'Selected ✓' : 'Add to trip'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleBook(offer)}
                      disabled={trackingId === offer.id}
                      style={{
                        padding: '8px 14px',
                        background: B.amber,
                        color: B.night,
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {trackingId === offer.id ? '…' : 'Book Now'}
                    </button>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
